import { useState, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Divider from "../../../components/Divider";
import formatDateTime from "../../../helpers/formatDateTime";
import useEventEmitter, { emitEvent } from "../../../hooks/useEventEmitter";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useUpdateMilestoneRevisionMutation from "../hooks/useUpdateMilestoneRevisionMutation";
import RevisedMilestoneList from "./RevisedMilestoneList";
import { fireSaveAndMoveNextTimelineEvent } from "./SaveAndMoveNextTimeline";
import {
  MILESTONE_REVISION_LOG,
  OPTIONAL_MILESTONE_REVISION_LOG,
} from "../types/CommonTypes";
import MilestoneClockPicker from "../../../components/MilestoneClockPicker";
import SideDrawer, { SideDrawerBody } from "../../../components/SideDrawer";
import isNotNull from "../../../helpers/isNotNull";
import { DATE_TYPE } from "../../../utils/Types";
import EditCaseSummaryDrawerFooter from "./EditCaseSummaryDrawerFooter";
import useUpdateOptionalMilestoneRevisionMutation from "../hooks/useUpdateOptionalMilestoneRevisionMutation";
import { getSelectedHospitalValue } from "../../hospitalModule/hooks/useSelectedHospitalValue";
import { DEFAULT_TIME_ZONE } from "../../../utils/Constants";
import useGetCaseDetailQuery from "../hooks/useGetCaseDetailQuery";
import InputText from "../../../components/InputText";

const EDIT_MILESTONE_TIME_DRAWER_EVENT = "EDIT_MILESTONE_TIME_DRAWER_EVENT";

export type EditMilestoneTimeDrawerProps = {
  caseId?: number;
  otId?: string;
  isOptionalMilestone?: boolean;
  milestoneId: number;
  milestoneUUID: string;
  milestoneName?: string;
  timelineItemIndex?: number;
  milestoneKey?: string;
  revision?: Array<MILESTONE_REVISION_LOG | OPTIONAL_MILESTONE_REVISION_LOG>;
  priorMilestoneName?: string;
  priorMilestoneTime?: DATE_TYPE;
  /** The milestone's current logged timestamp — used as the initial value in the picker */
  timestamp?: DATE_TYPE;
  /** Overrides the default "Edit Milestone Time" drawer title */
  drawerTitle?: string;
  /** Show/hide the "Milestone Revisions for X" heading. Defaults to true. */
  showDescription?: boolean;
  /** Show/hide the Revision History section. Defaults to true. */
  showRevisionHistory?: boolean;
};

export function toggleEditMilestoneTimeDrawer(
  data?: EditMilestoneTimeDrawerProps
) {
  emitEvent(EDIT_MILESTONE_TIME_DRAWER_EVENT, data);
}

const EditMilestoneTimeDrawer = () => {
  const { t } = useTranslation();
  const { firstName, LastName, userId, tenantId, hospitalId } = useAuthValue();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [milestoneDetail, setMilestoneDetail] =
    useState<EditMilestoneTimeDrawerProps>();

  const {
    caseId,
    otId,
    isOptionalMilestone,
    milestoneId,
    milestoneUUID,
    milestoneName,
    milestoneKey,
    timelineItemIndex,
    revision,
    priorMilestoneName,
    priorMilestoneTime,
    drawerTitle,
    showDescription = true,
    showRevisionHistory = true,
  } = milestoneDetail ?? {};

  const { data: caseDetailData } = useGetCaseDetailQuery(
    tenantId,
    hospitalId,
    caseId,
    Boolean(isOptionalMilestone && caseId && !otId),
  );

  const resolvedOtId = otId ?? caseDetailData?.otId;

  const isRoomCleanStart = milestoneName === t("Room_Clean_Start");
  const isRoomCleanEnd = milestoneName === t("Room_Clean_End");

  const { control, watch, reset, handleSubmit } = useForm({
    defaultValues: {
      revisedMilestoneTime: "",
      comment: "",
    },
  });

  const onClose = () => {
    reset();
    setIsDrawerOpen(false);
  };

  const {
    mutate: updateMilestoneRevisionMutate,
    isPending: isUpdatingMilestoneRevision,
  } = useUpdateMilestoneRevisionMutation();

  const {
    mutate: updateOptionalMilestoneRevisionMutate,
    isPending: isUpdatingOptionalMilestoneRevision,
  } = useUpdateOptionalMilestoneRevisionMutation();

  const revisionCount = useMemo(() => {
    return Math.max((revision?.length ?? 0) - 1, 0);
  }, [revision?.length]);

  const updateMilestone = (revisedMilestoneTime: DATE_TYPE, comment?: string) => {
    if (!milestoneUUID || !caseId || !milestoneId || !userId) {
      return;
    }
    const commonPayload = {
      caseId,
      milestoneId,
      milestoneUUID,
      milestoneRevisedByUserId: userId,
      milestoneRevisedByUserName: `${firstName} ${LastName}`,
      createdAt: new Date(),
      ...(comment ? { comment } : {}),
    };

    const payload = (() => {
      if (isRoomCleanEnd) {
        return {
          milestoneEndTime: revisedMilestoneTime,
          action: "end" as const,
        };
      }
      if (isRoomCleanStart) {
        return {
          milestoneStartTime: revisedMilestoneTime,
          action: "start" as const,
        };
      }
      return {
        milestoneEndTime: revisedMilestoneTime,
      };
    })();

    updateMilestoneRevisionMutate({
      ...commonPayload,
      ...payload,
    });
  };

  const updateOptionalMilestone = (
    revisedMilestoneTime: DATE_TYPE,
    comment?: string
  ) => {
    if (
      !(
        caseId &&
        milestoneId &&
        milestoneUUID &&
        milestoneName &&
        userId &&
        resolvedOtId
      )
    ) {
      return;
    }

    const payload = {
      caseId,
      optionalMilestoneId: milestoneId,
      milestoneUUID: milestoneUUID,
      milestoneEndTime: revisedMilestoneTime,
      milestoneRevisedByUserId: userId,
      milestoneRevisedByUserName: `${firstName} ${LastName}`,
      milestoneName,
      createdAt: new Date(),
      otId: resolvedOtId,
      ...(comment ? { comment } : {}),
    };

    updateOptionalMilestoneRevisionMutate(payload);
    reset();
  };

  const updateRevisedMilestones = (
    revisedMilestoneTime: DATE_TYPE,
    comment?: string
  ) => {
    if (!milestoneUUID || !caseId || !milestoneId || !userId) {
      return;
    }
    if (isOptionalMilestone) {
      updateOptionalMilestone(revisedMilestoneTime, comment);
    } else {
      updateMilestone(revisedMilestoneTime, comment);
    }
  };

  useEventEmitter(
    EDIT_MILESTONE_TIME_DRAWER_EVENT,
    (data?: EditMilestoneTimeDrawerProps) => {
      setIsDrawerOpen(true);

      if (!data) return;

      setMilestoneDetail(data);

      const revisions = data.revision ?? [];

      // Computed fresh from the incoming event's own milestoneName rather
      // than the component-level isRoomCleanStart/isRoomCleanEnd — those
      // are derived from milestoneDetail state, which this handler's own
      // setMilestoneDetail(data) call above hasn't flushed into a render
      // yet, so they'd still reflect whichever milestone was open before.
      const isRevisionRoomCleanStart =
        data.milestoneName === t("Room_Clean_Start");

      // The array's own order isn't guaranteed to be chronological (see
      // RevisedMilestoneList and CaseSummary's getLatestRevision, which both
      // re-derive the latest entry from createdAt/updatedAt rather than
      // trusting array order), so sort newest-first before picking a value
      // instead of assuming the last element is the latest revision.
      const sortedRevisions = [...revisions].sort((a, b) => {
        const aEntry = a as any;
        const bEntry = b as any;
        const aTime = new Date(
          aEntry?.createdAt ?? aEntry?.updatedAt ?? 0
        ).getTime();
        const bTime = new Date(
          bEntry?.createdAt ?? bEntry?.updatedAt ?? 0
        ).getTime();
        if (bTime !== aTime) return bTime - aTime;
        return (bEntry?.id ?? 0) - (aEntry?.id ?? 0);
      });

      let latestTime: string | null = null;

      for (const entry of sortedRevisions as any[]) {
        // A field not included in a PATCH is carried forward unchanged by
        // the backend rather than cleared, so a Room Clean Start entry
        // (which only ever submits milestoneStartTime) can still carry a
        // stale milestoneRevisedEndTime left over from an unrelated "end"
        // revision — that must not be preferred over the entry's own,
        // actually-current start time the way it correctly is everywhere
        // else (including Room Clean End).
        const primaryField = isRevisionRoomCleanStart
          ? entry?.milestoneRevisedStartTime
          : entry?.milestoneRevisedEndTime;
        const secondaryField = isRevisionRoomCleanStart
          ? entry?.milestoneRevisedEndTime
          : entry?.milestoneRevisedStartTime;

        if (primaryField) {
          latestTime = primaryField;
          break;
        }
        if (secondaryField) {
          latestTime = secondaryField;
          break;
        }
      }

      const rawTimestamp = data.timestamp;
      let fallbackTime: string;

      if (!rawTimestamp) {
        fallbackTime = new Date().toISOString();
      } else if (rawTimestamp instanceof Date) {
        fallbackTime = rawTimestamp.toISOString();
      } else {
        fallbackTime = String(rawTimestamp);
      }

      const initialTime =
        latestTime != null && latestTime !== "" ? latestTime : fallbackTime;

      reset({
        revisedMilestoneTime: initialTime,
        comment: "",
      });
    }
  );

  const onSubmit = (isSaveAndMoveNext?: boolean) =>
    handleSubmit((val) => {
      const trimmedComment = val?.comment?.trim();
      updateRevisedMilestones(
        val?.revisedMilestoneTime,
        trimmedComment || undefined
      );
      onClose();

      if (isSaveAndMoveNext && isNotNull(timelineItemIndex)) {
        fireSaveAndMoveNextTimelineEvent(timelineItemIndex, {
          savedMilestoneName: milestoneName,
          savedMilestoneKey: milestoneKey,
          savedMilestoneTime: val?.revisedMilestoneTime,
        });
      }
    })();

  const isSubmitDisabled = useMemo(() => {
    return (
      !watch("revisedMilestoneTime") ||
      isUpdatingMilestoneRevision ||
      isUpdatingOptionalMilestoneRevision ||
      revisionCount >= 5
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUpdatingMilestoneRevision,
    isUpdatingOptionalMilestoneRevision,
    revisionCount,
    showRevisionHistory,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch("revisedMilestoneTime"),
  ]);

  return (
    <SideDrawer
      visible={isDrawerOpen}
      onClose={onClose}
      title={drawerTitle ?? t("Edit_Milestone_Time")}
    >
      <SideDrawerBody>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: 0,
          }}
        >
          {priorMilestoneName && priorMilestoneTime && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.25,
                mb: 3,
                backgroundColor: "#E6E6E6",
                borderLeft: "4px solid #6C2BFF",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  lineHeight: "20px",
                  color: "text.primary",
                }}
              >
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  Prior Milestone:
                </Typography>{" "}
                {priorMilestoneName}{" "}
                {priorMilestoneTime ? ' at ' : ' '}
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {formatDateTime(
                    priorMilestoneTime,
                    true,
                    "hh:mm:ss aaa"
                  )}
                </Typography>
              </Typography>
            </Box>
          )}

          {showDescription && (
            <Typography
              sx={{
                fontSize: "18px",
                mb: 2.5,
                fontWeight: 400,
                color: "text.primary",
              }}
            >
              {t("Milestone_Revisions_for")}{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: "18px" }}
              >
                {milestoneName}
              </Typography>
            </Typography>
          )}

          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 4,
              borderRadius: "14px",
              mb: 3,
              border: "1px solid #9E9E9E",
              backgroundColor: "#FFF",
            }}
          >
            <Controller
              name="revisedMilestoneTime"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <MilestoneClockPicker
                  value={field.value}
                  onChange={field.onChange}
                  timezone={
                    getSelectedHospitalValue().timeZone ?? DEFAULT_TIME_ZONE
                  }
                  showDate
                  showSeconds
                />
              )}
            />
          </Paper>

          <InputText
            name="comment"
            control={control}
            label={t("Comment")}
            isLabelDefaultBehaviour
            sx={{
              borderRadius: 3,
              borderColor: "#9E9E9E",
              backgroundColor: "#FFF",
            }}
            multiline
            rows={3}
          />

          {showRevisionHistory && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "10px",
                mt: 1,
                mb: 4,
                backgroundColor: "#E6E6E6",
              }}
            >
              <RevisedMilestoneList currentMilestoneRevisons={revision} />
            </Paper>
          )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            pb: "18px",
            pt: 1,
          }}
        >
          <Divider sx={{ mt: 2, mb: 2, mx: -3 }} />
          <EditCaseSummaryDrawerFooter
            onSubmit={onSubmit}
            isSubmitDisabled={isSubmitDisabled}
          />
        </Box>
      </SideDrawerBody>
    </SideDrawer>
  );
};

export default EditMilestoneTimeDrawer;
